// frontend/src/design-system/components/DataTable.tsx
import * as React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn } from '../../lib/utils';

export interface DataTableColumn<T> {
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedRowKey?: string;
  emptyState?: React.ReactNode;
  loading?: boolean;
}

export function DataTable<T>({
  columns, rows, rowKey, onRowClick, selectedRowKey, emptyState, loading,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading…</div>
    );
  }
  if (rows.length === 0 && emptyState) return <>{emptyState}</>;
  return (
    <div className="rounded-2xl bg-card ring-1 ring-border/50 shadow-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 border-b border-border/60">
            {columns.map((c) => (
              <TableHead
                key={c.key}
                className={cn(
                  'px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground',
                  c.headerClassName,
                )}
              >
                {c.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const k = rowKey(row);
            const selected = selectedRowKey === k;
            return (
              <TableRow
                key={k}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); }
                } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
                data-state={selected ? 'selected' : undefined}
                className={cn(
                  'border-b border-border/40 last:border-b-0 transition-colors hover:bg-accent/40',
                  onRowClick && 'cursor-pointer',
                  selected && 'bg-accent/50',
                )}
              >
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={cn('px-4 py-4 text-sm text-foreground align-middle', c.className)}
                  >
                    {c.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
